import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { RadioButton } from "primereact/radiobutton";
import getHealthQuoteBanner from "../../assets/images/get-health-quotes-banner.png";
import { getProfile } from "../../utils/backend";
import FloatLabelInput from "../../components/FloatingLabel";
import FloatingSelect from "../../components/FloatingSelect";
import FloatDateInput from "../../components/FloatDateInput";
import BottomMessagePopUp from "../../components/BottomMessagePopUp";
import AppOnlyNotice from "../../components/AppOnlyNotice";
import useStoreQuoteHealth from "../../store/actions/store-quote-health-details";
import useStoreToken from "../../store/actions/store-token";
import useGetToken from "../../store/selectors/get-token";
import { useTranslation } from 'react-i18next';

import getQuoteHealthDetails from "../../store/selectors/get-quote-health-details";

const GetHealthQuotes = () => {
	const [policyHolderName, setPolicyHolderName] = useState("");
	const [idType, setIdType] = useState("");
	const [idNo, setIdNo] = useState("");
	const [dob, setDob] = useState(new Date());
	const [age, setAge] = useState("");
	const [gender, setGender] = useState("");
	const [occupation, setOccupation] = useState("");
	const [ph_company_name, setPhCompanyName] = useState("");
	const [isSmoker, setIsSmoker] = useState("");
	const [isManual, setIsManual] = useState("");
	const [roomBoard, setRoomBoard] = useState("");
	const [loading, setLoading] = useState(false);
	const [agreement, setAgreement] = useState(false);
	const navigate = useNavigate();
	const [errorMessage, setErrorMessage] = useState("");
	const [isErrorVisible, setIsErrorVisible] = useState(false);
	const showErrorMessage = (msg) => {
		setErrorMessage(msg);
		setIsErrorVisible(true);
	};
	const storeQuoteHealthDetails = useStoreQuoteHealth();
	const storeToken = useStoreToken();
	const storedToken = useGetToken();
	const { t, i18n } = useTranslation();

	// Only the ANSA app opens this page with ?token=. A visitor without one keeps the token already
	// stored instead of having it overwritten with null.
	const [tokenParam] = useState(() => new URLSearchParams(window.location.search).get("token"));
	const token = tokenParam || storedToken;

	const getHealthDetails = getQuoteHealthDetails();

	const PRIVACY_POLICY_URL = process.env.REACT_APP_PRIVACY_POLICY_URL;

	useEffect(() => {
		if (tokenParam) storeToken(tokenParam);
	}, [tokenParam]);

	useEffect(() => {
			
		if (getHealthDetails) {
			setIdType(getHealthDetails?.id_type || "");
			setIdNo(getHealthDetails?.id_no || "");
			setDob(new Date(getHealthDetails?.dob || new Date())); // Parse the date
			setAge(getHealthDetails?.age || "");
			setGender(getHealthDetails?.gender || "");
			setOccupation(getHealthDetails?.occupation || "");
			setPhCompanyName(getHealthDetails?.ph_company_name || "");
			setIsSmoker(getHealthDetails?.is_smoker || "");
			setIsManual(getHealthDetails?.is_dangerous_expose || "");
			setRoomBoard(getHealthDetails?.room_board || "");
		}

		const cachedAgreement = localStorage.getItem("healthAgreement");
		if (cachedAgreement !== null) setAgreement(JSON.parse(cachedAgreement));
		
	}, [getHealthDetails]);

	const idTypeOptions = [
		// { value: "", label: "Please select type of identity" },
		{ value: "nric", label: t("common.nric") },
		{ value: "passport", label: t("common.passport") },
	];

	const genderOptions = [
		// { value: "", label: "Please select Gender" },
		{ value: "male", label: t("common.male") },
		{ value: "female", label: t("common.female") },
	];

	const roomBoardOptions = [
		// { value: "", label: "Please select room & board" },
		{ value: "100", label: "100" },
		{ value: "120", label: "120" },
		{ value: "150", label: "150" },
		{ value: "180", label: "180" },
		{ value: "250", label: "250" },
		{ value: "350", label: "350" },
		{ value: "500", label: "500" },
	];

	const handleIdTypeSelectChange = (event) => {
		setIdType(event.target.value);
		if (event.target.value === 'passport') {
			setGender('');
		} else if ((event.target.value === 'nric' || event.target.value === 'NRIC') && idNo.length === 12) {
			setGenderFromNric(idNo);
		}
	};

	const handleGenderSelectChange = (event) => {
		setGender(event.target.value);
	};

	const handleRoomBoardSelectChange = (event) => {
		setRoomBoard(event.target.value);
	};

	useEffect(() => {
		fetchData();
	}, [getProfile]);

	const fetchData = async () => {
		if (token) {
			const response = await getProfile(token);
			if (response.success) {
				if (response.data.preferred_language) {
					i18n.changeLanguage(response?.data?.preferred_language);
				}
				if (response.data.full_name) {
					setPolicyHolderName(response?.data?.full_name);
				}
				if (response.data.id_type) {
					const IdentType = (response?.data?.id_type === 'nric' || response?.data?.id_type === 'NRIC') ? 'nric' : 'passport';
					setIdType(IdentType);
				}
				if (response.data.id_no) {
					if (response?.data?.id_type === 'nric' || response?.data?.id_type === 'NRIC') {
						const birthdate = setDOBFromNric(response.data.id_no);
						setDob(birthdate);

						const age = setAgeFromNric(response.data.id_no);
						setAge(age);
						setGenderFromNric(response.data.id_no);
					}
					setIdNo(response?.data?.id_no);
				}
			}
		}
	};

	const handleIDNoBlur = () => {
		if ((idType === "nric" || idType === "NRIC") && idNo !== "" && idNo.trim().length === 12) {
			const nric = idNo.trim();
			
			// Get year from NRIC (first 2 digits)
			const year = parseInt(nric.slice(0, 2), 10);
			const month = parseInt(nric.slice(2, 4), 10) - 1; // Months are 0-based
			const day = parseInt(nric.slice(4, 6), 10);
			
			// Determine century (if year >= 50, assume 1900s, else 2000s)
			const fullYear = year >= 50 ? 1900 + year : 2000 + year;
			
			// Set DOB
			const birthDate = new Date(fullYear, month, day);
			setDob(birthDate);
			
			// Calculate age
			const today = new Date();
			const currentYear = today.getFullYear();
			let calculatedAge = currentYear - fullYear;
			
			// Adjust age if birthday hasn't occurred this year
			const birthMonth = month;
			const currentMonth = today.getMonth();
			const birthDay = day;
			const currentDay = today.getDate();
			
			if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
				calculatedAge--;
			}
			
			setAge(calculatedAge);
		}
	};

	const setDOBFromNric = (nric) => {
		if (!nric || nric.length < 6) return null;
	
		const year = parseInt(nric.slice(0, 2), 10);
		const month = parseInt(nric.slice(2, 4), 10) - 1;
		const day = parseInt(nric.slice(4, 6), 10);
	
		const fullYear = year >= 50 ? 1900 + year : 2000 + year;
	
		return new Date(fullYear, month, day);
	};

	const setAgeFromNric = (nric) => {
		if (!nric || nric.length < 2) return null;

		const year = parseInt(nric.slice(0, 2), 10);
		const fullYear = year >= 50 ? 1900 + year : 2000 + year;

		const currentYear = new Date().getFullYear();

		return currentYear - fullYear;
	};

	const setGenderFromNric = (nric) => {
		if (!nric || nric.length !== 12) return;
		
		const lastDigit = parseInt(nric.slice(-1));
		if (isNaN(lastDigit)) return;
		
		setGender(lastDigit % 2 === 0 ? 'female' : 'male');
	};

	const onAgreementChange = (e) => {
		setAgreement(e.checked);
		localStorage.setItem("healthAgreement", JSON.stringify(e.checked));
	};

	const clearFields = () => {
		setPolicyHolderName("");
		setIdType("");
		setIdNo("");
		setDob(new Date());
		setAge("");
		setGender("");
		setOccupation("");
		setIsSmoker("");
		setRoomBoard("");

		const cachedKeys = [
			"healthAgreement",
      "quoteHealthDetails",
			"healthPlan",
			"healthQuestionnaire",
			"order_details_id",
      "frontImageID",
      "backImageID",
      "frontImagePassport",
      "declarationOne",
      "nominees",
      "declarationTwoA1",
      "declarationTwoA2",
    ];

    cachedKeys.forEach((key) => localStorage.removeItem(key));
	};

	const handleButtonClick = async (e) => {
		e.preventDefault();

		if ((idType === 'nric' || idType === 'NRIC') && idNo.length !== 12) {
			alert(t(`messages.enterValidNRICNo`));
			return;
		}
		
		if (!agreement) {
			alert(t(`messages.youMustAgreePrivacy`));
			return;
		}

		setLoading(true);

		try {
			const payloads = {
				policy_holder_name: policyHolderName,
				id_type: idType,
				id_no: idNo,
				dob: dob,
				age: age,
				gender: gender,
				occupation: occupation,
				ph_company_name: ph_company_name,
				is_smoker: isSmoker,
				is_dangerous_expose: isManual,
				room_board: roomBoard,
			};

			storeQuoteHealthDetails(payloads);
	
			navigate(`/get-health-quotes/select-health-plan`);
		} catch (error) {
			showErrorMessage(t(`messages.encounteredError`));
		} finally {
			setLoading(false);
		}
	};

	const handleMoreAboutInsuranceClick = () => {
		navigate("/get-health-quotes/more-about-ansa");
	};

	const handleAnsaHelpClick = (e) => {
		e.preventDefault();

		navigate(token ? `/ansa-help?token=${token}` : `/ansa-help`);
	};

	const handleIdNoChange = (e) => {
		const value = e.target.value;
		
		if (idType === 'nric' || idType === 'NRIC') {
			const numbersOnly = value.replace(/[^\d]/g, '');
			if (numbersOnly.length <= 12) {
				setIdNo(numbersOnly);
				
				if (numbersOnly.length === 12) {
					setGenderFromNric(numbersOnly);
				}
			}
		} else {
			setIdNo(value);
			setGender('');
		}
	};

	// Add this new function to calculate age from a date
	const calculateAge = (birthDate) => {
		const today = new Date();
		let age = today.getFullYear() - birthDate.getFullYear();
		const monthDiff = today.getMonth() - birthDate.getMonth();
		
		if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}
		
		return age;
	};

	// Update the DOB change handler
	const handleDobChange = (e) => {
		const newDob = e.value;
		setDob(newDob);
		
		if (newDob) {
			const calculatedAge = calculateAge(newDob);
			setAge(calculatedAge);
		}
	};

	const handleAgeChange = (e) => {
		const value = e.target.value;

		if (/^\d*$/.test(value)) {
			setAge(value);
		}
	};

	return (
		<>
			<div className="content">
				<div className="get-quote-container d-m-column vw-50">
					{/* Right Section */}
					<div className="get-quote-form-section w-100">
						<div className="get-quote-image-section">
							<img
								className="get-quote-banner"
								src={getHealthQuoteBanner}
								alt="get-quote-banner"
								style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
							/>
						</div>

						{!token ? (
							<AppOnlyNotice
								title={t(`healthInsurance.appOnlyTitle`)}
								body={t(`healthInsurance.appOnlyBody`)}
								enquiryText={t(`healthInsurance.appOnlyEnquiry`)}
								enquiryLinkText={t(`healthInsurance.appOnlyEnquiryLink`)}
								enquiryService="Health Insurance"
							/>
						) : (
						<>
						<div className="d-flex flex-row justify-content-between w-100 mt-10">
							<div className="w-70 text-left">
								<h3 className="mt-0 mb-0">{t(`healthInsurance.title`)}</h3>
							</div>
							<div className="w-30 text-right">
								<span className="color-primary underline" onClick={clearFields}>
									{t(`common.clearFields`)}
								</span>
							</div>
							<p className="formatted-text">{t(`healthInsurance.subTitle`)}</p>
						</div>

						<form className="form-input-get-quote w-100">
							<div className="form-input-get-quote-group">
								<div className="row justify-content-between mx-0">
									<div className="col-lg-6 input-group">
										<FloatLabelInput
											label={
												<>
													{t(`common.policyHolderName`)}
													<span className="required-asterisk">*</span>
												</>
											}
											value={policyHolderName}
											onChange={(e) => setPolicyHolderName(e.target.value)}
											placeholder=""
										/>
									</div>

									<div className="col-lg-6 input-group">
										<FloatingSelect
											label={
												<>
													{t(`common.typeOfIdentity`)}
													<span className="required-asterisk">*</span>
												</>
											}
											options={idTypeOptions}
											value={idType}
											onChange={handleIdTypeSelectChange}
										/>
									</div>
								</div>

								<div className="input-group">
									<FloatLabelInput
										label={
											<>
												{idType === 'passport' ? t(`common.passportNumber`) : t(`common.identificationNumber`)}
												<span className="required-asterisk">*</span>
											</>
										}
										value={idNo}
										onChange={handleIdNoChange}
										placeholder=""
										onBlur={handleIDNoBlur}
										maxLength={(idType === 'nric' || idType === 'NRIC') ? 12 : undefined}
									/>
								</div>

								<div className="row justify-content-between mx-0">
									<div className="col-lg-6 col-sm-6 input-group">
										<FloatDateInput
											label={
												<>
													{t(`common.dateOfBirth`)}
													<span className="required-asterisk">*</span>
												</>
											}
											value={dob}
											onChange={handleDobChange}
											dateFormat="dd-mm-yy"
											showIcon
										/>
									</div>

									<div className="col-lg-6 col-sm-6 input-group">
										<FloatLabelInput
											label={
												<>
													{t(`common.age`)}
													<span className="required-asterisk">*</span>
												</>
											}
											value={age}
											onChange={handleAgeChange}
											placeholder=""
										/>
									</div>
								</div>

								<div className="row justify-content-between mx-0">
									<div className="col-lg-6 col-sm-6 input-group">
										<FloatingSelect
											label={t(`common.gender`)}
											options={genderOptions}
											value={gender}
											onChange={handleGenderSelectChange}
											disabled={(idType === 'nric' || idType === 'NRIC') && idNo.length === 12}
										/>
									</div>

									<div className="col-lg-6 col-sm-6 input-group">
										<FloatLabelInput
											label={t(`common.occupation`)}
											value={occupation}
											onChange={(e) => setOccupation(e.target.value)}
											placeholder=""
										/>
									</div>
								</div>

								<div className="input-group">
									<FloatLabelInput
										label={t(`common.employerName`)}
										value={ph_company_name}
										onChange={(e) => setPhCompanyName(e.target.value)}
										placeholder=""
									/>
								</div>

								<div className="d-flex flex-column flex-wrap gap-0 mb-2 w-100">
									<label className="vw-100">{t(`healthInsurance.areYouSmoker`)}</label>
									<div className="vw-100 d-flex flex-row">
										<div className="flex align-items-center col-lg-6 col-sm-6">
											<RadioButton
												inputId="smokerYes"
												name="yes"
												value={"yes"}
												onChange={(e) => setIsSmoker(e.value)}
												checked={isSmoker === "yes"}
											/>
											<label htmlFor="smokerYes" className="ml-2">
												{t(`common.yes`)}
											</label>
										</div>
										<div className="flex align-items-center col-lg-6 col-sm-6">
											<RadioButton
												inputId="smokerNo"
												name="no"
												value={"no"}
												onChange={(e) => setIsSmoker(e.value)}
												checked={isSmoker === "no"}
											/>
											<label htmlFor="smokerNo" className="ml-2">
												{t(`common.no`)}
											</label>
										</div>
									</div>
								</div>

								<div className="d-flex flex-column flex-wrap gap-0 w-100">
									<label className="vw-100">{t(`healthInsurance.doesYourJobInvolveManualWork`)}</label>
									<div className="vw-100 d-flex flex-row">
										<div className="flex align-items-center col-lg-6 col-sm-6">
											<RadioButton
												inputId="ManualWorkYes"
												name="yes"
												value={"yes"}
												onChange={(e) => setIsManual(e.value)}
												checked={isManual === "yes"}
											/>
											<label htmlFor="ManualWorkYes" className="ml-2">
												{t(`common.yes`)}
											</label>
										</div>
										<div className="flex align-items-center col-lg-6 col-sm-6">
											<RadioButton
												inputId="ManualWorkNo"
												name="no"
												value={"no"}
												onChange={(e) => setIsManual(e.value)}
												checked={isManual === "no"}
											/>
											<label htmlFor="ManualWorkNo" className="ml-2">
												{t(`common.no`)}
											</label>
										</div>
									</div>
								</div>

								<div className="row input-group justify-content-between mx-0">
									<FloatingSelect
										label={
											<>
												{t(`healthInsurance.room&Board`)}
												<span className="required-asterisk">*</span>
											</>
										}
										options={roomBoardOptions}
										value={roomBoard}
										onChange={handleRoomBoardSelectChange}
									/>
								</div>
							</div>

							<div className="mb-3">
								<div className="flex">
									<Checkbox
										inputId="agreement"
										name="agreement"
										value={agreement}
										onChange={onAgreementChange}
										checked={agreement}
									/>
									<label htmlFor="agreement" className="ml-2">
										{t(`healthInsurance.getQuoteAgreement`)} {` `}
										<a
											href={PRIVACY_POLICY_URL}
											className="color-primary"
											target="_blank"
											rel="noopener noreferrer"
										>
											{t(`healthInsurance.privacyNotice`)}
										</a>
										.
									</label>
								</div>
							</div>

							<Button
								label={t(`healthInsurance.getQuoteNow`)}
								className="get-quote-button"
								loading={loading}
								disabled={!agreement || !roomBoard || !idNo || !idType || !age || !dob || !policyHolderName}
								onClick={handleButtonClick}
								size="large"
							/>
						</form>
						</>
						)}
					</div>
					<div
						className="pt-4"
						style={{
							width: "100vw",
							padding: "0",
							margin: "0",
							overflowX: "auto",
							whiteSpace: "nowrap",
							display: "flex",
							gap: "10px",
						}}
					>
						<Card
							className="d-flex flex-column border-radius-15"
							style={{
								width: "280px",
								flex: "0 0 auto",
								display: "inline-block",
								marginRight: "10px",
							}}
						>
							<h4 className="mb-10">{t(`healthInsurance.moreAboutAnsa.title`)}</h4>
							<p
								className="formatted-text"
								style={{
									whiteSpace: "normal",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{t(`healthInsurance.moreAboutAnsa.subTitle`)}
							</p>
							<Button
								label={t(`healthInsurance.moreAboutAnsa.button`)}
								className="btn-primary-outline"
								loading={loading}
								onClick={handleMoreAboutInsuranceClick}
								size="large"
							/>
						</Card>

						<Card
							className="d-flex flex-column border-radius-15"
							style={{
								width: "280px",
								flex: "0 0 auto",
								display: "inline-block",
								marginRight: "10px",
							}}
						>
							<h4 className="mb-10">{t(`healthInsurance.needMoreInfo.title`)}</h4>
							<p
								className="formatted-text"
								style={{
									whiteSpace: "normal",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{t(`healthInsurance.needMoreInfo.subTitle`)}
							</p>
							<Button
								label={t(`healthInsurance.needMoreInfo.button`)}
								className="btn-primary-outline"
								loading={loading}
								onClick={(e) => handleAnsaHelpClick(e)}
								size="large"
							/>
							{/* BottomMessagePopUp Component */}
							<BottomMessagePopUp
								message={errorMessage}
								type="error"
								isVisible={isErrorVisible}
								onClose={() => setIsErrorVisible(false)}
							/>
						</Card>
					</div>
				</div>
			</div>
		</>
	);
};

export default GetHealthQuotes;
